import { useMemo } from "react";
import { Button, Flex, Icon, Icons, type TableColumnType } from "@ir/ui";
import {
  MasterDataProductCategory,
  type IMasterDataProductCategory,
  type MasterDataProductCategory as MasterDataProductCategoryEntity,
} from "@src/domain/entities/masterData";
import { Permissions } from "@src/domain/permissions";
import HasAnyPermission from "@src/presentation/components/permissions/HasAnyPermission";
import {
  MASTER_DATA_COLUMN_VERTICAL_ALIGN,
  MasterDataListTable,
  renderMasterDataStatusChip,
  renderMasterDataTextCell,
} from "./masterDataTable.shared";

export interface MasterDataProductCategoryRow extends IMasterDataProductCategory {
  key: string;
}

function mapCategoriesToRows(
  categories: MasterDataProductCategoryEntity[],
): MasterDataProductCategoryRow[] {
  return categories.map((entity) => ({
    ...MasterDataProductCategory.toJSON(entity),
    key: String(entity.productCategoryId),
  }));
}

function buildColumns(
  onEdit: (category: MasterDataProductCategoryEntity) => void,
  categoriesById: Map<number, MasterDataProductCategoryEntity>,
): TableColumnType<MasterDataProductCategoryRow>[] {
  return [
    {
      title: "Category Name",
      dataIndex: "name",
      key: "name",
      align: "left",
      verticalAlign: MASTER_DATA_COLUMN_VERTICAL_ALIGN,
      width: 260,
      fixed: "left",
    },
    {
      title: "Category Key",
      dataIndex: "categoryKey",
      key: "categoryKey",
      align: "left",
      verticalAlign: MASTER_DATA_COLUMN_VERTICAL_ALIGN,
      width: 200,
      fixed: "left",
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      align: "left",
      verticalAlign: MASTER_DATA_COLUMN_VERTICAL_ALIGN,
      width: 320,
      cellRender: renderMasterDataTextCell,
    },
    {
      title: "Status",
      dataIndex: "isActive",
      key: "isActive",
      align: "left",
      verticalAlign: MASTER_DATA_COLUMN_VERTICAL_ALIGN,
      width: 150,
      cellRender: renderMasterDataStatusChip,
    },
    {
      title: "Search Vector Keywords",
      dataIndex: "searchVectorKeywords",
      key: "searchVectorKeywords",
      align: "left",
      verticalAlign: MASTER_DATA_COLUMN_VERTICAL_ALIGN,
      width: 200,
      cellRender: renderMasterDataTextCell,
    },
    {
      title: "Actions",
      dataIndex: "productCategoryId",
      key: "actions",
      align: "center",
      fixed: "right",
      verticalAlign: MASTER_DATA_COLUMN_VERTICAL_ALIGN,
      width: 80,
      cellRender: (value: unknown) => {
        const id = Number(value);
        const category = categoriesById.get(id);
        if (!category) return null;
        return (
          <HasAnyPermission
            requiredPermissions={[Permissions.MASTER_DATA_EDIT]}
          >
            <Flex align="center" justifyContent="center">
              <Button
                size="small"
                variant="outline"
                color="grey"
                aria-label={`Edit ${category.name}`}
                onPress={() => onEdit(category)}
              >
                <Icon name={Icons.Edit} size="small" />
              </Button>
            </Flex>
          </HasAnyPermission>
        );
      },
    },
  ];
}

interface MasterDataProductCategoriesTableProps {
  productCategories: MasterDataProductCategoryEntity[];
  search: string;
  setSearch: (search: string) => void;
  setPage: (page: number) => void;
  onEdit: (category: MasterDataProductCategoryEntity) => void;
}

export const MasterDataProductCategoriesTable = ({
  productCategories,
  search,
  setSearch,
  setPage,
  onEdit,
}: MasterDataProductCategoriesTableProps) => {
  const categoriesById = useMemo(() => {
    const map = new Map<number, MasterDataProductCategoryEntity>();
    for (const category of productCategories) {
      map.set(category.productCategoryId, category);
    }
    return map;
  }, [productCategories]);

  const columns = useMemo(
    () => buildColumns(onEdit, categoriesById),
    [onEdit, categoriesById],
  );
  const dataSource = useMemo(
    () => mapCategoriesToRows(productCategories),
    [productCategories],
  );

  return (
    <MasterDataListTable
      columns={columns}
      dataSource={dataSource}
      search={search}
      entityLabel="product categories"
      ariaLabel="Product Categories table"
      setSearch={setSearch}
      setPage={setPage}
    />
  );
};
